import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, RefreshCw, Redo2 } from "lucide-react";

const STATE_COLOR = {
  SUCCEEDED: "#166534",
  FAILED: "#B91C1C",
  NEEDS_ATTENTION: "#B97700",
  AWAITING_APPROVAL: "#1D4ED8",
  HELD: "#B97700",
  RUNNING: "#1D4ED8",
  QUEUED: "#6B7280",
  PENDING: "#6B7280",
};

function formatDuration(startedAt, finishedAt) {
  if (!startedAt) return "—";
  if (!finishedAt) return "in flight";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 90) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatK(n) {
  if (!n || n < 1000) return String(n || 0);
  return `${(n / 1000).toFixed(1)}k`;
}

export default function AdminRunDetail() {
  const { runId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/admin/pipeline/runs/${runId}`)
      .then(async (r) => {
        setData(r.data);
        const bId = r.data?.run?.brand_id;
        if (bId) {
          try {
            const b = await http.get(`/admin/brands`);
            const match = (b.data.brands || []).find((x) => x.id === bId);
            setBrand(match || null);
          } catch { /* non-blocking */ }
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Run not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [runId]); // eslint-disable-line

  // Enqueue a fresh REPROCESS run for this brand. Reuses the same
  // endpoint + in-flight guard as the "Force crawl now" button on the
  // brand edit page. On success, deep-link to the new run so the user
  // stays inside the "inspect a run" loop.
  const retryRun = async () => {
    if (!data?.run?.brand_id) return;
    setRetrying(true);
    try {
      const { data: res } = await http.post(
        `/admin/pipeline/reprocess/${data.run.brand_id}`,
      );
      toast.success(`New run enqueued (${res.run_id.slice(0, 8)}…)`);
      nav(`/admin/pipeline/runs/${res.run_id}`);
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        const existingId = typeof d === "object" ? d.run_id : null;
        const state = typeof d === "object" ? d.state : null;
        toast.message(
          `Run already in flight${state ? ` — state: ${state}` : ""}. Opening it.`,
        );
        if (existingId) nav(`/admin/pipeline/runs/${existingId}`);
      } else {
        toast.error(e.response?.data?.detail || "Retry failed");
      }
    } finally { setRetrying(false); }
  };

  if (loading) {
    return <AdminLayout><div className="bp-container py-24 text-sm" data-testid="run-detail-loading">Loading run…</div></AdminLayout>;
  }
  if (!data?.run) {
    return (
      <AdminLayout>
        <div className="bp-container py-24 text-sm" data-testid="run-detail-notfound">
          Run not found. <Link to="/admin/pipeline" className="bp-link">Back to Pipeline →</Link>
        </div>
      </AdminLayout>
    );
  }

  const run = data.run;
  const cost = run.cost_counters || {};
  const inTok = cost.total_input_tokens || 0;
  const outTok = cost.total_output_tokens || 0;
  const calls = cost.llm_calls || 0;
  const stateColor = STATE_COLOR[run.state] || "var(--bp-text-muted)";

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        {/* Header */}
        <div className="flex items-baseline justify-between mb-6">
          <div>
            {brand ? (
              <Link
                to={`/admin/brands/${brand.slug}`}
                className="text-xs uppercase tracking-widest bp-link inline-flex items-center gap-1"
                data-testid="run-detail-back-to-brand"
              >
                <ArrowLeft size={12} /> {brand.display_name}
              </Link>
            ) : (
              <Link to="/admin/pipeline" className="text-xs uppercase tracking-widest bp-link inline-flex items-center gap-1">
                <ArrowLeft size={12} /> Pipeline
              </Link>
            )}
            <h1 className="font-serif text-4xl mt-2" data-testid="run-detail-title">Run detail</h1>
            <div className="text-xs font-mono mt-1" style={{ color: "var(--bp-text-muted)" }}>
              {run.id} · kind: {run.kind || "—"} · v{run.pipeline_version || "—"}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {(run.state === "FAILED" || run.state === "NEEDS_ATTENTION") && (
              <button
                onClick={retryRun}
                disabled={retrying}
                className="text-sm bp-link inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="run-detail-retry"
                title="Enqueue a fresh REPROCESS run for this brand"
              >
                <Redo2 size={14} /> {retrying ? "Enqueuing…" : "Retry this run"}
              </button>
            )}
            <button
              onClick={load}
              className="text-sm bp-link inline-flex items-center gap-1"
              data-testid="run-detail-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border p-4" style={{ borderColor: "var(--bp-border)" }} data-testid="tile-state">
            <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>State</div>
            <div className="font-mono text-lg mt-1" style={{ color: stateColor }}>{run.state}</div>
            <div className="font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>outcome: {run.outcome || "—"}</div>
          </div>
          <div className="bg-white border p-4" style={{ borderColor: "var(--bp-border)" }} data-testid="tile-duration">
            <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Duration</div>
            <div className="font-mono text-lg mt-1">{formatDuration(run.started_at, run.finished_at)}</div>
            <div className="font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
              stage: {run.stage || "—"}
            </div>
          </div>
          <div className="bg-white border p-4" style={{ borderColor: "var(--bp-border)" }} data-testid="tile-llm">
            <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>LLM cost</div>
            <div className="font-mono text-lg mt-1">{calls} <span className="text-xs">calls</span></div>
            <div className="font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
              {formatK(inTok)} in · {formatK(outTok)} out
            </div>
          </div>
          <div className="bg-white border p-4" style={{ borderColor: "var(--bp-border)" }} data-testid="tile-artefacts">
            <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Artefacts</div>
            <div className="font-mono text-lg mt-1">{(data.snapshots || []).length} snap</div>
            <div className="font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
              {(data.evidences || []).length} evidences · {(data.discarded || []).length} discarded
            </div>
          </div>
        </div>

        {run.held_reason && (
          <div
            className="p-4 mb-6 border"
            style={{ borderColor: "#B97700", background: "#FFF8E1", color: "#7A5700" }}
            data-testid="run-detail-held-reason"
          >
            <div className="uppercase text-[10px] tracking-widest mb-1">Held reason</div>
            <div className="text-sm font-mono break-all">{run.held_reason}</div>
          </div>
        )}

        {/* Discovery summary — how many URLs did the frontier see,
            how many did it keep, how many did it discard. Populated
            from the `discovery.done` event on the run. Renders only
            when the pipeline reached the discovery stage. */}
        {data.discovery && (
          <section className="mb-8" data-testid="discovery-summary-section">
            <h2 className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>
              Discovered vs kept
            </h2>
            <div
              className="bg-white border p-4"
              style={{ borderColor: "var(--bp-border)" }}
              data-testid="discovery-summary-card"
            >
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-center">
                {[
                  ["Candidates found", data.discovery.candidates_found, "raw URLs the frontier saw"],
                  ["After dedup", data.discovery.candidates_after_dedup, "after collapsing language / duplicate variants"],
                  ["Sitemap URLs", data.discovery.sitemap_urls_found, "URLs pulled from sitemap.xml"],
                  ["Kept", data.discovery.kept, "URLs that made it to fetch stage"],
                  ["Discarded", data.discovery.discarded, "see full list in the Discarded pages section"],
                  ["LLM skipped", data.discovery.llm_skipped, "URLs classified without an LLM call"],
                ].map(([label, value, hint]) => (
                  <div key={label} data-testid={`discovery-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="uppercase text-[9px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{label}</div>
                    <div className="font-mono text-xl mt-1">{value ?? 0}</div>
                    <div className="text-[10px] mt-1" style={{ color: "var(--bp-text-muted)" }}>{hint}</div>
                  </div>
                ))}
              </div>
              {data.discovery.no_domains && (
                <div
                  className="mt-3 p-2 text-xs"
                  style={{ background: "#FEE2E2", color: "#B91C1C" }}
                  data-testid="discovery-no-domains-flag"
                >
                  ⚠︎ No official_domains on the brand — the frontier had nothing to seed. Fix the website / domains on the brand page then re-crawl.
                </div>
              )}
              {data.discovery.lanes_kept && Object.keys(data.discovery.lanes_kept).length > 0 && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--bp-border)" }}>
                  <div className="uppercase text-[10px] tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>
                    Kept by lane
                  </div>
                  <div className="font-mono text-xs flex flex-wrap gap-x-4 gap-y-1">
                    {Object.entries(data.discovery.lanes_kept).map(([lane, n]) => (
                      <span key={lane}>{lane}: {n}</span>
                    ))}
                  </div>
                </div>
              )}
              {(data.discovery.seed_roots || []).length > 0 && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--bp-border)" }}>
                  <div className="uppercase text-[10px] tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>
                    Seed roots
                  </div>
                  <ul className="text-xs font-mono space-y-1">
                    {data.discovery.seed_roots.map((s, i) => (
                      <li key={`${s.url}-${i}`} className="break-all" data-testid={`seed-root-${i}`}>
                        <span style={{ color: s.blocked ? "#B91C1C" : "#166534" }}>
                          {s.blocked ? "✗" : "✓"}
                        </span>{" "}
                        {s.url || "—"}
                        <span className="ml-2" style={{ color: "var(--bp-text-muted)" }}>
                          {s.status ? `status ${s.status}` : ""}
                          {s.mode ? ` · ${s.mode}` : ""}
                          {s.reason ? ` · ${s.reason}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Snapshots */}
        <section className="mb-8">
          <h2 className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>
            Evidence snapshots ({(data.snapshots || []).length})
          </h2>
          {(data.snapshots || []).length === 0 ? (
            <div
              className="bg-white border p-4 text-xs"
              style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}
              data-testid="snapshots-empty"
            >
              No snapshots captured in this run.
            </div>
          ) : (
            <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
              <ul className="divide-y" style={{ borderColor: "var(--bp-border)" }}>
                {(data.snapshots || []).map((s) => (
                  <li key={s.id} className="p-4" data-testid={`snapshot-${s.id}`}>
                    <div className="flex items-start gap-2">
                      <ExternalLink size={12} className="mt-1 shrink-0" />
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="bp-link break-all text-sm">
                        {s.title || s.url}
                      </a>
                    </div>
                    <div className="mt-1 font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
                      {s.compose_path || s.fetch_path || "—"} · captured {s.captured_at || "—"}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Evidences */}
        <section className="mb-8">
          <h2 className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>
            Evidence records ({(data.evidences || []).length})
          </h2>
          {(data.evidences || []).length === 0 ? (
            <div
              className="bg-white border p-4 text-xs"
              style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}
              data-testid="evidences-empty"
            >
              No evidences extracted in this run.
            </div>
          ) : (
            <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
              <ul className="divide-y" style={{ borderColor: "var(--bp-border)" }}>
                {(data.evidences || []).map((e) => (
                  <li key={e.id} className="p-4" data-testid={`evidence-${e.id}`}>
                    <div className="flex flex-wrap items-baseline gap-2 text-[10px] font-mono" style={{ color: "var(--bp-text-muted)" }}>
                      <span className="uppercase tracking-widest">{e.category}</span>
                      {e.specificity && <span>· {e.specificity}</span>}
                      {typeof e.extraction_confidence === "number" && <span>· conf {e.extraction_confidence.toFixed(2)}</span>}
                      {e.register_verified && <span>· reg: {e.register_verified}</span>}
                    </div>
                    <div className="mt-1 text-sm">{e.evidence_excerpt}</div>
                    {e.source_url && (
                      <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs bp-link break-all">
                        {e.source_url}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Discarded */}
        {(data.discarded || []).length > 0 && (
          <section className="mb-8">
            <h2 className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>
              Discarded pages ({(data.discarded || []).length})
            </h2>
            <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                    <th className="px-3 py-2 uppercase tracking-widest">URL</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Reason</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.discarded || []).map((d, i) => (
                    <tr
                      key={`${d.url || "row"}-${i}`}
                      className="border-b last:border-b-0"
                      style={{ borderColor: "var(--bp-border)" }}
                      data-testid={`discarded-row-${i}`}
                    >
                      <td className="px-3 py-2 font-mono break-all max-w-md">{d.url || "—"}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{d.reason || "—"}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{d.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Raw events (collapsed) */}
        {run.events && run.events.length > 0 && (
          <details className="mt-6" data-testid="run-events-details">
            <summary className="cursor-pointer text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
              Raw events ({run.events.length})
            </summary>
            <pre
              className="mt-2 p-4 bg-white border text-[10px] overflow-auto max-h-96"
              style={{ borderColor: "var(--bp-border)" }}
            >
              {JSON.stringify(run.events, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </AdminLayout>
  );
}
